import {
	issueRichTextToPlainText,
	IssueType,
	PORTAL_ISSUE_MESSAGE_MAX_LENGTH,
	PORTAL_ISSUE_SUBJECT_MAX_LENGTH,
	PORTAL_ISSUE_TYPES,
} from '@supply-mover-2-0/shared';
import {
	issueImageFileIds,
	IssueImagePicker,
	issueImagesUploading,
	IssueRichTextEditor,
	Select,
	useTranslation,
	type PendingIssueImageModel,
} from '@supply-mover-2-0/web-ui';
import type { JSX } from 'react';
import { useCallback, useId, useState } from 'react';
import { usePortalAuth } from '../hooks/usePortalAuth';
import {
	submitPortalIssue,
	uploadPortalIssueImage,
} from '../services/portalIssueService';
import {
	saveSupportNotifyPreferences,
	SupportNotifyControl,
	type SupportNotifyValueModel,
} from './SupportNotifyControl';

const INPUT_CLASSES =
	'w-full px-3 py-2 rounded-lg bg-brand-input border border-brand-border text-sm text-default';

/**
 * The support-request form (PD-13705, extracted in PD-15002) — category /
 * subject / message with shared server-mirrored limits. Used by the
 * dashboard Submit-an-Issue widget and the Support page's New Support
 * Request modal, so the two entry points can never drift.
 *
 * Owns the field + submit state; the caller decides what happens after a
 * successful submission via onSuccess (the widget shows its reference
 * screen, the Support page navigates to the new request's detail).
 */
export function SupportRequestForm({
	onSuccess,
	submitLabel,
}: {
	onSuccess: (issueId: number) => void;
	/** Defaults to 'Send' (the widget's historical label). */
	submitLabel?: string;
}): JSX.Element | null {
	const { t } = useTranslation();
	const { token } = usePortalAuth();
	const categoryLabelId = useId();

	const [type, setType] = useState<IssueType>(IssueType.QUESTION);
	const [subject, setSubject] = useState('');
	// PD-16475 — the message as rich text, plus images.
	const [messageHtml, setMessageHtml] = useState('');
	const [images, setImages] = useState<Array<PendingIssueImageModel>>([]);
	const [submitting, setSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	// PD-16475 — how to be told about replies. Saved only once the request
	// is sent: that is when the customer has agreed to texts, if they left
	// Text checked.
	const [notifyValue, setNotifyValue] =
		useState<SupportNotifyValueModel | null>(null);

	const uploadImage = useCallback(
		async (file: File) => {
			if (token == null) {
				throw new Error(t('Please sign in again.'));
			}

			return uploadPortalIssueImage(token, file);
		},
		[token, t],
	);

	if (token == null) {
		return null;
	}

	// The server counts the visible text, not the markup, against the limit.
	const messageText = issueRichTextToPlainText(messageHtml);

	const handleSubmit = async (): Promise<void> => {
		const trimmedSubject = subject.trim();

		if (trimmedSubject.length === 0 || messageText.trim().length === 0) {
			setError(t('Please fill in a subject and a message.'));
			return;
		}

		if (messageText.length > PORTAL_ISSUE_MESSAGE_MAX_LENGTH) {
			setError(
				t('Your message is too long (max {{max}} characters).', {
					max: String(PORTAL_ISSUE_MESSAGE_MAX_LENGTH),
				}),
			);
			return;
		}

		if (issueImagesUploading(images)) {
			setError(t('Please wait for your images to finish uploading.'));
			return;
		}

		setSubmitting(true);
		setError(null);

		try {
			const result = await submitPortalIssue(token, {
				subject: trimmedSubject,
				type,
				message: messageText,
				messageHtml,
				attachmentFileIds: issueImageFileIds(images),
			});
			if (notifyValue != null) {
				// Never throws; a failed save leaves the previous choices.
				await saveSupportNotifyPreferences(notifyValue);
			}
			setSubject('');
			setMessageHtml('');
			setImages([]);
			setType(IssueType.QUESTION);
			onSuccess(result.issueId);
		} catch {
			setError(t("We couldn't send your request right now. Please try again."));
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<form
			className="flex-1 flex flex-col gap-3"
			onSubmit={(e) => {
				e.preventDefault();
				void handleSubmit();
			}}
		>
			{/* PD-15027 — the shared Select, styled like the rest of the
			    storefront. Labelled by id rather than wrapped in a <label>: a
			    label around the dropdown's button would forward clicks on the
			    caption into it. The button is type="button", so it never
			    submits the form. */}
			<div>
				<span
					id={categoryLabelId}
					className="block text-xs font-medium text-brand-muted mb-1"
				>
					{t('Category')}
				</span>
				<Select
					aria-labelledby={categoryLabelId}
					value={type}
					onChange={(next) => {
						setType(next as IssueType);
					}}
					disabled={submitting}
					options={PORTAL_ISSUE_TYPES.map((issueType) => ({
						value: issueType,
						label: t(issueType),
					}))}
				/>
			</div>

			<label className="block">
				<span className="block text-xs font-medium text-brand-muted mb-1">
					{t('Subject')}
				</span>
				<input
					type="text"
					value={subject}
					onChange={(e) => {
						setSubject(e.target.value);
					}}
					maxLength={PORTAL_ISSUE_SUBJECT_MAX_LENGTH}
					placeholder={t('What is this about?')}
					disabled={submitting}
					className={INPUT_CLASSES}
				/>
			</label>

			<div className="block flex-1 flex flex-col">
				<span className="block text-xs font-medium text-brand-muted mb-1">
					{t('Message')}
				</span>
				<IssueRichTextEditor
					value={messageHtml}
					onChange={setMessageHtml}
					placeholder={t('Tell us what you need help with...')}
					disabled={submitting}
					minHeight="96px"
					className="bg-brand-input border-brand-border"
				/>
				<IssueImagePicker
					className="mt-2"
					value={images}
					onChange={setImages}
					upload={uploadImage}
					disabled={submitting}
				/>
			</div>

			<SupportNotifyControl mode="deferred" onChange={setNotifyValue} />

			{error != null && <p className="text-xs text-danger">{error}</p>}

			<button
				type="submit"
				disabled={submitting || issueImagesUploading(images)}
				className="w-full px-4 py-2 rounded-lg bg-portal-accent text-white text-sm font-medium hover:bg-portal-accent-hover disabled:opacity-50"
			>
				{submitting ? t('Sending...') : t(submitLabel ?? 'Send')}
			</button>
		</form>
	);
}
